import Component from '@glimmer/component';
import { action, get } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { later } from '@ember/runloop';
import PS from 'mewe/utils/pubsub';
import { Theme, Target } from 'mewe/constants';
import { getOwner } from '@ember/application';
import { openPostbox } from 'mewe/utils/dialogs-common';
import QuestNotifications from './quest-notifications';
import { getPublicProfileParams } from 'mewe/utils/miscellaneous-utils';
import { installDropdown } from 'mewe/utils/dropdown-utils';
import Storage from 'mewe/shared/storage';

export default class NewNavigation extends Component {
  @service account;
  @service router;
  @service analytics;
  @service features;
  @service dynamicDialogs;

  @tracked isSmartSearchActive = false;
  @tracked dropdownOptions;
  @tracked notificationsDropdown;

  constructor() {
    super(...arguments);
    this.questNotifications = new QuestNotifications(this);

    installDropdown(this, 'notificationsDropdown', {
      refFunctionName: 'setDropdownRefNotifications',
      onUpdate: (state) => {
        if (state === true) {
          this.analytics.sendEvent('notificationCenterOpened', {
            has_unseen: !!this.args.model.notifications.unseenCount,
          });
        }
      },
    });

    installDropdown(this, 'dropdownOptions', {
      refFunctionName: 'setDropdownRefOptions',
      onUpdate: (state) => {
        if (state === true) {
          this.questNotifications.destroyQuestHintToast();
          this.questNotifications.cleanAfterQuestHint();
        }
      },
    });
  }

  @action
  onDestroy() {
    this.questNotifications.destroy();
  }

  get showCartIndicator() {
    const storeState = this.args.model.storeState;

    // getter is important here, otherwise property won't update
    if (get(storeState, 'storeItemsLoaded')) {
      return storeState.cartItems.length;
    } else {
      let lsCartItems = [];
      try {
        lsCartItems = JSON.parse(Storage.get(Storage.keys.cartItems)) || [];
      } catch (e) {}

      return lsCartItems.length;
    }
  }

  @action
  setSmartSearchState(isActive) {
    this.isSmartSearchActive = isActive;

    if (isActive) {
      // delay for better UX, there is 150ms long transition for mobile view
      later(() => PS.Pub('open.smart.search'), 100);
    }
  }

  @action
  goToRegister() {
    this.router.transitionTo('register', { queryParams: getPublicProfileParams(this.args.model) });
  }

  @action
  goToLogin() {
    this.router.transitionTo('login', { queryParams: getPublicProfileParams(this.args.model) });
  }

  @action
  navItemClicked() {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }

  @action
  openPostbox() {
    let theme = this.args.model.theme;
    let groupId, eventId, page,
      target = Target.CONTACTS;

    // posting from Group/Event/Page view should go to that specific scope,
    // all other cases should post to Contacts
    if (theme === Theme.GROUP) {
      const appGroup = getOwner(this).lookup('route:app.group');
      const group = appGroup?.get('currentModel.group');
      if (group && group.canPost) {
        groupId = appGroup.get('currentModel.groupId');
        target = Target.GROUP;
      } else {
        theme = Theme.CONTACTS;
      }
    } else if (theme === Theme.GROUPS) {
      // just theme, target has to be selected in postbox
      target = Theme.GROUP;
    } else if (theme === Theme.EVENT) {
      const appEvent = getOwner(this).lookup('route:app.event');
      const event = appEvent?.get('currentModel.event');
      if (event && event.canPost) {
        eventId = appEvent.get('currentModel.eventId');
        target = Target.EVENT;
      } else {
        theme = Theme.CONTACTS;
      }
    } else if (theme === Theme.PAGE) {
      const appPage = getOwner(this).lookup('route:app.publicid');
      const modelPage = appPage?.get('currentModel.page');
      if (modelPage && modelPage.isOwnerAdmin) {
        target = Target.PAGE;
        page = modelPage;
      } else {
        theme = Theme.CONTACTS;
      }
    } else {
      target = Target.CONTACTS;
      theme = Theme.CONTACTS;
    }

    openPostbox(
      {
        theme: this.args.model.theme,
        target: target,
        groupId,
        eventId,
        page,
      },
      this.dynamicDialogs
    );
  }
}